import React from 'react'
import { useDispatch } from 'react-redux'
import { Text } from 'uiComponents/typography'
import { TableColumn } from 'uiComponents/table/reactTable'
import { DiscountCodesElement } from 'orders/helpers'
import { specifyOrderStatus, IOrder, IOrderPayment } from 'orders/schema'
import Money from 'uiComponents/money'
import { State } from 'store'
import InlineVisitDate from '../../commons/inlineVisitDate'
import InlineVisitTime from '../../commons/inlineVisitTime'
import InlineEmail from '../../commons/inlineEmail'
import OrderId from '../../commons/orderId'
import styledComponent from 'styled-components'
import StatusLabel from '../../commons/orderStatusLabel'
import { useHasFeatureFunc } from 'utils/useHasFeature'
import { BaseRouteParams, useNavigation } from 'hocs'
import { useParams } from 'react-router-dom'
import { Checkbox } from 'uiComponents/input'
import {
    getIsOrderRowCheckboxDisabled,
    getOrderItems,
    getSelectedOrderById,
    getSelectedOrderItemsByTableId,
    getSelectedOrdersList,
    toggleAllOrderItems,
    toggleAllOrders,
    toggleOrder,
} from 'orders/orders/redux'
import { formatTimestamp, getDiscountCodesString, getLastPayment } from 'orders/orders/utils'
import { ChevronDownSolid, ChevronUpSolid } from '@convious/icons'
import { useAppSelector } from 'store/hooks'
import { useTranslation } from 'translations'

const ClosedIcon = styledComponent(ChevronDownSolid)`
  color: ${(props) => props.theme.colors.aluminiumShades[10]};
  width: 20px;
  height: 20px;
`
const OpenedIcon = styledComponent(ChevronUpSolid)`
  color: ${(props) => props.theme.colors.aluminiumShades[30]};
  width: 20px;
  height: 20px;
`

export interface OrdersColumn extends TableColumn<IOrder> {
    withTimeSlots?: boolean
}

const paymentStatusesToIgnore = ['expired', 'open', 'pending', 'failed']
export const getPaymentToDisplay = (payments: IOrderPayment[]) => {
    const relevantPayments = payments.filter((payment) => !paymentStatusesToIgnore.includes(payment.paymentStatus))
    return getLastPayment(relevantPayments)
}

export const getDistinctPaymentMethods = (payments: IOrderPayment[]): string[] => {
    const relevantPayments = payments.filter((payment) => !paymentStatusesToIgnore.includes(payment.paymentStatus))
    return [...new Set(relevantPayments.map((payment) => payment.paymentMethod).filter(Boolean))]
}

const FILTER_PROMOTED_COLUMNS: Record<string, string> = {
    email_status: 'emailStatus',
    status: 'orderStatus',
    location_id: 'location',
}

export const useOrdersColumns = () => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const accounts = useAppSelector((state) => (state.auth.user ? state.auth.user.accounts : []))
    const activeAccount = accounts.find((x) => x.slug === accountSlug) || accounts[0]
    const withTimeSlots = activeAccount ? activeAccount.timeSlotsEnabled : false
    const hasFeature = useHasFeatureFunc()
    const dispatch = useDispatch()
    const columnsConfig = useAppSelector((state: State) => state.orderTable.tableColumns)
    const navigation = useNavigation()
    const { filter } = navigation.query()

    const promotedColumnKeys = React.useMemo(() => {
        if (!filter || typeof filter !== 'string') {
            return []
        }
        return filter
            .split(';')
            .map((pair) => FILTER_PROMOTED_COLUMNS[pair.split(':')[0]])
            .filter(Boolean)
    }, [filter])

    const effectiveColumnsConfig = React.useMemo(() => {
        if (!promotedColumnKeys.length) {
            return columnsConfig
        }
        const withForced = columnsConfig.map((column) =>
            promotedColumnKeys.includes(column.key) ? { ...column, selected: true } : column,
        )
        const promoted = withForced.filter((column) => promotedColumnKeys.includes(column.key))
        const rest = withForced.filter((column) => !promotedColumnKeys.includes(column.key))
        const anchorIndex = rest.findIndex((column) => column.key === 'email')
        return [...rest.slice(0, anchorIndex + 1), ...promoted, ...rest.slice(anchorIndex + 1)]
    }, [columnsConfig, promotedColumnKeys])

    const columns = React.useMemo(() => {
        const dataColumns = {
            location: {
                Header: t('orders.orders.ordersTable.columns.location'),
                accessor: 'location.name',
                disableSortBy: true,
                width: '7.3rem',
            },
            id: {
                Header: t('orders.orders.ordersTable.columns.id'),
                accessor: 'number',
                width: '6rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ row: { original } }) => <OrderId order={original} />,
            },
            email: {
                Header: t('orders.orders.ordersTable.columns.email'),
                accessor: 'customer.email',
                width: '13rem',
                disableHideColumn: true,
                Cell: ({ row: { original } }) => <InlineEmail order={original} limitedTextWidth="12.5em" />,
            },
            quantity: {
                Header: t('orders.orders.ordersTable.columns.qty'),
                accessor: 'quantity',
                width: '4.5rem',
                disableHideColumn: true,
                disableSortBy: true,
            },
            subtotalInclTax: {
                Header: hasFeature('OrdersListPricesExcludingTax') ? t('orders.orders.ordersTable.columns.subtotalInclTax') : t('orders.orders.ordersTable.columns.subtotal'),
                accessor: 'totalPriceInclTax',
                width: '9.6rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ value }) => <Money amount={value} accountSlug={accountSlug} />,
            },
            subtotalExclTax: {
                Header: t('orders.orders.ordersTable.columns.subtotalExclTax'),
                accessor: 'totalPriceExclTax',
                features: ['OrdersListPricesExcludingTax'],
                width: '9.8rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ value }) => <Money amount={value} accountSlug={accountSlug} />,
            },
            bookingFeesInclTax: {
                Header: hasFeature('OrdersListPricesExcludingTax') ? t('orders.orders.ordersTable.columns.feesInclTax') : t('orders.orders.ordersTable.columns.fees'),
                accessor: 'totalBookingFeeInclTax',
                features: ['BookingFees'],
                width: '8.5rem',
                disableSortBy: true,
                Cell: ({ value }) => <Money amount={value} accountSlug={accountSlug} />,
            },
            bookingFeesExclTax: {
                Header: t('orders.orders.ordersTable.columns.feesExclTax'),
                accessor: 'totalBookingFeeExclTax',
                features: ['BookingFees', 'OrdersListPricesExcludingTax'],
                width: '8.5rem',
                disableSortBy: true,
                Cell: ({ value }) => <Money amount={value} accountSlug={accountSlug} />,
            },
            discountAmount: {
                Header: t('orders.orders.ordersTable.columns.discount'),
                accessor: 'discountAmount',
                width: '7rem',
                disableSortBy: true,
                Cell: ({ value }) => <Money amount={value > 0 ? `-${value}` : value} accountSlug={accountSlug} />,
            },
            discount_code: {
                Header: t('orders.orders.ordersTable.columns.code'),
                accessor: 'discount_code',
                disableSortBy: true,
                width: '8rem',
                Cell: ({ row }) => {
                    const discountCodesString = getDiscountCodesString(row.original)
                    return (
                        <span title={discountCodesString} className="truncated-text">
                            <DiscountCodesElement discountsString={discountCodesString} />
                        </span>
                    )
                },
            },
            totalExclTax: {
                Header: t('orders.orders.ordersTable.columns.totalExclTax'),
                accessor: 'totalDiscountedPriceExclTax',
                features: ['OrdersListPricesExcludingTax'],
                width: '9.1rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ value }) => <Money amount={value} accountSlug={accountSlug} />,
            },
            totalInclTax: {
                Header: hasFeature('OrdersListPricesExcludingTax') ? t('orders.orders.ordersTable.columns.totalInclTax') : t('orders.orders.ordersTable.columns.total'),
                accessor: 'totalDiscountedPriceInclTax',
                width: '8rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ value }) => <Money amount={value} accountSlug={accountSlug} />,
            },
            order_date: {
                Header: t('orders.orders.ordersTable.columns.saleDate'),
                accessor: 'order_date',
                width: '9.5rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    return original.orderDate ? formatTimestamp(original.orderDate, original.timezone) : '-'
                },
            },
            payment_date: {
                Header: t('orders.orders.ordersTable.columns.paymentUpdatedAt'),
                accessor: 'payment_date',
                width: '10.5rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    const lastPayment = getPaymentToDisplay(original.payments)
                    return lastPayment?.paymentDate ? formatTimestamp(lastPayment.paymentDate, original.timezone) : '-'
                },
            },
            visit_date: {
                Header: t('orders.orders.ordersTable.columns.visitDate'),
                accessor: 'visit_date',
                width: '8rem',
                disableHideColumn: true,
                Cell: ({ row: { original } }) => <InlineVisitDate order={original} />,
            },
            visit_time: {
                Header: t('orders.orders.ordersTable.columns.eventTime'),
                accessor: 'visit_time',
                width: '8rem',
                withTimeSlots: true,
                disableHideColumn: true,
                Cell: ({ row: { original } }) => <InlineVisitTime order={original} />,
            },
            orderStatus: {
                Header: t('orders.orders.ordersTable.columns.orderStatus'),
                accessor: 'detailedStatus',
                width: '11rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    const status = specifyOrderStatus(original)
                    const reservationWithError = Object.values(original.confirmReservationsResult || {}).find(
                        (reservation) => reservation.error,
                    )
                    const errorMessage = original.issuedBarcodesResult?.error || reservationWithError?.error || ''
                    return <StatusLabel status={status} title={errorMessage} />
                },
            },
            paymentStatus: {
                Header: t('orders.orders.ordersTable.columns.paymentStatus'),
                accessor: 'paymentStatus',
                width: '8rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    const lastPayment = getPaymentToDisplay(original.payments)

                    return <StatusLabel status={lastPayment?.paymentStatus ?? 'unpaid'} />
                },
            },
            paymentMethod: {
                Header: t('orders.orders.ordersTable.columns.paymentMethod'),
                accessor: 'paymentMethod',
                width: '9.3rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ row: { original } }) => {
                    const paymentMethods = getDistinctPaymentMethods(original.payments)
                    return (
                        <Text style={{ textTransform: 'capitalize' }}>
                            {paymentMethods.length > 0 ? paymentMethods.join(', ') : '-'}
                        </Text>
                    )
                },
            },
            reseller: {
                Header: t('orders.orders.ordersTable.columns.reseller'),
                accessor: 'reseller.name',
                width: '8rem',
                feature: ['ResellersFeature'],
                disableSortBy: true,
                Cell: ({ value }) => <span>{value || '-'}</span>,
            },
            emailStatus: {
                Header: t('orders.orders.ordersTable.columns.emailStatus'),
                accessor: 'emailStatus',
                width: '9.3rem',
                disableHideColumn: true,
                disableSortBy: true,
                Cell: ({ value }) => (
                    <Text style={{ textTransform: 'capitalize' }}>
                        {value === 'failed' || value === 'disabled' ? (
                            <span style={{ color: 'red' }}>{value}</span>
                        ) : (
                            <>{value}</>
                        )}
                    </Text>
                ),
            },
            downloaded: {
                Header: t('orders.orders.ordersTable.columns.downloaded'),
                accessor: 'downloaded',
                width: '11rem',
                disableSortBy: true,
                Cell: ({ value, row: { original } }) => (value ? formatTimestamp(value, original.timezone) : '-'),
            },
        } as Record<string, OrdersColumn>

        const _columns = [
            {
                Header: () => null,
                id: 'expander',
                accessor: 'expander',
                width: '2.5rem',
                disableSortBy: true,
                disableHideColumn: true,
                Cell: ({ row }) => (
                    <div {...row.getToggleRowExpandedProps()}>{row.isExpanded ? <OpenedIcon /> : <ClosedIcon />}</div>
                ),
            },
            {
                accessor: 'selectable',
                width: '2.5rem',
                disableSortBy: true,
                disableHideColumn: true,
                Header: ({ rows }) => {
                    const selectedOrders = useAppSelector(getSelectedOrdersList).length

                    return (
                        <Checkbox
                            checked={selectedOrders > 0 || selectedOrders === rows.length}
                            data-testid="select-all-orders-checkbox"
                            onChange={(e) => {
                                dispatch(
                                    toggleAllOrders(
                                        rows.reduce((acc, row) => {
                                            return { ...acc, [row.original.id]: e.target.checked }
                                        }, {}),
                                    ),
                                )
                            }}
                            indeterminateState={selectedOrders > 0 && selectedOrders !== rows.length}
                        />
                    )
                },
                Cell: ({ row }) => {
                    const isSelected = useAppSelector(getSelectedOrderById(row.original.id))
                    const orderItems = useAppSelector(getOrderItems(row.original.id)) ?? []
                    const disabled = useAppSelector(getIsOrderRowCheckboxDisabled(row.original.id))
                    const selectedOrderItems = useAppSelector(getSelectedOrderItemsByTableId(row.original.id))

                    return (
                        <Checkbox
                            className="select-order"
                            data-testid="orders-select-order-checkbox"
                            checked={Object.values(selectedOrderItems).some((x) => x) || isSelected}
                            indeterminateState={!isSelected}
                            onChange={(e) => {
                                dispatch(toggleOrder({ orderUuid: row.original.id, selected: e.target.checked }))

                                dispatch(
                                    toggleAllOrderItems({
                                        tableId: row.original.id,
                                        rows: orderItems.reduce(
                                            (acc: { [itemUuid: string]: boolean }, item) => ({
                                                ...acc,
                                                [item.id]: !!item.refundInfo ? false : e.target.checked,
                                            }),
                                            {},
                                        ),
                                    }),
                                )
                            }}
                            disabled={disabled}
                        />
                    )
                },
            },
            ...effectiveColumnsConfig.filter((column) => column.selected).map((column) => dataColumns[column.key]),
        ] as OrdersColumn[]

        return _columns.filter((column: OrdersColumn) => {
            if (column.features) {
                return column.features.every((feature) => hasFeature(feature))
            }

            if (!!column.withTimeSlots) {
                return withTimeSlots
            }

            return true
        })
    }, [accountSlug, withTimeSlots, effectiveColumnsConfig])

    return columns
}
